import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { powersOfAttorney, type PowerOfAttorney } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'documents.powers_of_attorney.create';

const STATE_LABEL: Record<string, string> = {
  ACTIVE: 'Активна', USED: 'Використана', EXPIRED: 'Прострочена', REVOKED: 'Відкликана',
};

export default function PowersOfAttorneyPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)), [perms.availableOrgs],
  );
  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['powers-of-attorney', orgId],
    queryFn: () => powersOfAttorney.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<PowerOfAttorney>[]>(
    () => [
      { key: 'document_number', header: 'Номер', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'document_date', header: 'Дата', width: 110, sortValue: (r) => r.document_date ?? '', cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '' },
      { key: 'authorized_actions', header: 'Дозволено', sortValue: (r) => r.authorized_actions ?? '', cell: (r) => r.authorized_actions },
      { key: 'valid_from', header: 'Дійсна з', width: 110, sortValue: (r) => r.valid_from ?? '', cell: (r) => r.valid_from },
      { key: 'valid_until', header: 'по', width: 110, sortValue: (r) => r.valid_until ?? '', cell: (r) => r.valid_until },
      { key: 'state', header: 'Стан', width: 130, sortValue: (r) => r.state ?? '', cell: (r) => STATE_LABEL[r.state] ?? r.state },
    ], [],
  );
  const canCreate = !!orgId && allowedOrgIds.has(orgId);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Довіреності</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <button className="primary" style={{ marginLeft: 'auto' }} disabled={!canCreate}
          onClick={() => navigate(`/settlements/powers-of-attorney/new?org=${orgId}`)}>
          + Довіреність
        </button>
      </div>
      <DataTable<PowerOfAttorney>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/settlements/powers-of-attorney/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
